import { useForm } from 'react-hook-form';
import React, { useState } from 'react';
import styled from 'styled-components';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import axios from 'axios';

const Link = styled.a`
    color: #007bff;
    text-decoration: none;
    cursor: pointer;

    &:hover {
        color: #0056b3;
        text-decoration: underline;
    }
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #f5f5f5;
    max-width: 500px;
    width: 100vw;
    margin: 0 auto;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    @media (max-width: 768px) {
        width: 95vw;
    }

    @media (max-width: 480px) {
        width: 80vw;
    }
`;

const FormFileUploadContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 10px;
`;

const FormFileUploadLabel = styled.label`
    width: 150px;
    text-align: center;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #fff;
`;

const ResultsContainer = styled.div`
    margin-top: 20px;
    padding: 20px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #f9f9f9;
    width: 100%;
    -webkit-box-sizing: border-box; /* Safari/Chrome, other WebKit */
    -moz-box-sizing: border-box;    /* Firefox, other Gecko */
    box-sizing: border-box;

    & > h3 {
        margin: 0 0 10px;
        padding: 0;

        &:after {
            content: '';
            display: block;
            width: 100%;
            height: 1px;
            background-color: #ddd;
            margin-top: 10px;
        }
    }
`;

const ResultItem = styled.p`
    margin: 0 0 10px;
    padding: 0;
    color: #333;
`;

const Input = styled.input`
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    width: 100%;
    cursor: pointer;
`;

const Button = styled.button`
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 5px;
    background-color: #fff;
    cursor: pointer;
    width: 100%;
`;

const LoadingOverlay = styled.div`
    display: ${props => props.isUploading ? 'flex' : 'none'};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    justify-content: center;
    align-items: center;
    z-index: 1;
`;

LoadingOverlay.defaultProps = {
    isUploading: false,
};

const Spinner = styled.div`
    border: 16px solid #f3f3f3;
    border-top: 16px solid #3498db;
    border-radius: 50%;
    width: 80px;
    height: 80px;
    animation: spin 2s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const PEOPLE_CSV_TEMPLATE = [
    { person_name: 'Raj Patel', email: 'raj.patel@gmail.com', phone: '8471234567', role: 'Adult', family_name: 'Patel Family' },
    { person_name: 'Anita Patel', email: 'anita.patel@gmail.com', phone: '8477654321', role: 'Adult', family_name: 'Patel Family' },
    { person_name: 'Vikram Patel', email: 'vikram.patel@gmail.com', phone: '', role: 'Child', family_name: 'Patel Family' },
    { person_name: 'Sarita Patel', email: 'sarita.patel@gmail.com', phone: '', role: 'Child', family_name: 'Patel Family' },
    { person_name: 'Harpreet Dhillon', email: 'harpreet.dhillon@gmail.com', phone: '2121234567', role: 'Adult', family_name: 'Dhillon Family' },
    { person_name: 'Manpreet Dhillon', email: 'manpreet.dhillon@gmail.com', phone: '2127654321', role: 'Adult', family_name: 'Dhillon Family' },
    { person_name: 'Jaspreet Dhillon', email: '', phone: '2124567890', role: 'Adult', family_name: 'Dhillon Family' },
    { person_name: 'Rajveer Dhillon', email: '', phone: '', role: 'Child', family_name: 'Dhillon Family' },
    { person_name: 'Ishaan Dhillon', email: '', phone: '', role: 'Child', family_name: 'Dhillon Family' },
];

const INVITIATIONS_CSV_TEMPLATE = [
    { event_name: 'Haldi & Sangeet', family_name: 'Patel Family', max_kids: '0', max_adults: '2', notes: 'This is a custom note just for the Haldi & Sangeet.' },
    { event_name: 'Haldi & Sangeet', family_name: 'Dhillon Family', max_kids: '0', max_adults: '3', notes: '' },
    { event_name: 'Sikh Ceremony', family_name: 'Patel Family', max_kids: '2', max_adults: '2', notes: 'This is a custom note just for the Sikh Ceremony.' },
    { event_name: 'Sikh Ceremony', family_name: 'Dhillon Family', max_kids: '2', max_adults: '3', notes: '' },
    { event_name: 'Hindu Ceremony', family_name: 'Patel Family', max_kids: '0', max_adults: '2', notes: 'This is a custom note just for the Hindu Ceremony.' },
    { event_name: 'Hindu Ceremony', family_name: 'Dhillon Family', max_kids: '0', max_adults: '3', notes: '' },
    { event_name: 'Reception', family_name: 'Patel Family', max_kids: '0', max_adults: '2', notes: 'This is a custom note just for the Reception.' },
    { event_name: 'Reception', family_name: 'Dhillon Family', max_kids: '2', max_adults: '3', notes: '' },
];

const UploadDataPage = () => {
    // User uploaded files
    const [invitationsCsvFile, setInvitationsCsvFile] = useState(null);
    const [peopleCsvFile, setPeopleCsvFile] = useState(null);

    // Uploading state
    const [isUploading, setIsUploading] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);
    const [error, setError] = useState(null);

    const [isUnlocked, setIsUnlocked] = useState(false);
    const { register, handleSubmit, watch, formState: { errors } } = useForm();
    const password = watch("password", "");

    const onSubmit = (data) => {
        setError(null);
        if (data.password === process.env.REACT_APP_SYNC_PASSWORD) {
            setIsUnlocked(true);
        } else {
            setError('Invalid password');
        }
    }

    const arraysAreEqual = (a, b) => {
        return a.length === b.length && a.every((val, index) => val === b[index]);
    };

    const handleInvitationsCsvFileChange = (e) => {
        setInvitationsCsvFile(e.target.files[0]);
    }

    const handlePeopleCsvFileChange = (e) => {
        setPeopleCsvFile(e.target.files[0]);
    }

    const handleUploadData = () => {
        setError(null);
        setIsUploading(true);

        // Define the expected headers for each CSV file
        const expectedInvitationsHeaders = ['event_name', 'family_name', 'max_kids', 'max_adults', 'notes'];
        const expectedPeopleHeaders = ['person_name', 'email', 'phone', 'role', 'family_name'];

        // Parse the CSV files and check the headers
        Papa.parse(invitationsCsvFile, {
            complete: (results) => {
                const headers = results.data[0];
                if (!arraysAreEqual(headers, expectedInvitationsHeaders)) {
                    console.error('Invalid headers in Invitations CSV file');
                    setError('Invalid CSV uploaded for invitations.');
                    return;
                }

                Papa.parse(peopleCsvFile, {
                    complete: (results) => {
                        const headers = results.data[0];
                        if (!arraysAreEqual(headers, expectedPeopleHeaders)) {
                            console.error('Invalid headers in People CSV file');
                            setError('Invalid CSV uploaded for people.');
                            return;
                        }
        
                        // If the headers are valid, upload the data
                        const uploadData = async () => {
                            const readFile = (file) => {
                                return new Promise((resolve, reject) => {
                                    const reader = new FileReader();
                                    reader.onload = (event) => {
                                        const base64File = event.target.result.split(",")[1];
                                        resolve(base64File);
                                    };
                                    reader.onerror = (error) => reject(error);
                                    reader.readAsDataURL(file);
                                });
                            };
                            
                            try {
                                const [peopleCsvBase64, invitationsCsvBase64] = await Promise.all([
                                    readFile(peopleCsvFile),
                                    readFile(invitationsCsvFile)
                                ]);
                        
                                const response = await axios.post(`${process.env.REACT_APP_API_URL}/sync`, {
                                    people_csv_data: peopleCsvBase64,
                                    invitations_csv_data: invitationsCsvBase64
                                });
                        
                                if (response.status !== 200) {
                                    console.error(response);
                                    setError(`An error occurred while uploading the data: ${response.statusText}`);
                                } else {
                                    console.log('Data uploaded successfully', response.data);
                                    setUploadResults(response.data);
                                    setInvitationsCsvFile(null);
                                    setPeopleCsvFile(null);
                                }
                            } catch (error) {
                                console.error(error);
                            }
                        }

                        uploadData().finally(() => {
                            setIsUploading(false);
                        });
                    },
                });
            },
        });
    }

    const handleDownloadTemplate = (filename, data) => {
        const csv = Papa.unparse(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        saveAs(blob, filename);
    };

    return (
        <>
            {!isUnlocked ? (
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Input type="password" placeholder="Password" {...register("password", { required: true })} />
                    {error && <p style={{ color: 'red', marginTop: 0, marginBottom: 0 }}>{error}</p>}
                    <Button type="submit">Submit</Button>
                </Form>
            ) : (
                <Form>
                    <LoadingOverlay isUploading={isUploading}>
                        <Spinner />
                    </LoadingOverlay>
                    <h2 style={{ margin: 0 }}>Upload Data</h2>
                    <p style={{ margin: 0 }}>Upload the CSV files to update the database</p>
                    <FormFileUploadContainer>
                        <FormFileUploadLabel>Invitations</FormFileUploadLabel>
                        <Input type="file" accept=".csv" onChange={handleInvitationsCsvFileChange} />
                        <Link onClick={() => handleDownloadTemplate('InvitationsTemplate.csv', INVITIATIONS_CSV_TEMPLATE)}>Download Template</Link>
                    </FormFileUploadContainer>
                    <FormFileUploadContainer>
                        <FormFileUploadLabel>People</FormFileUploadLabel>
                        <Input type="file" accept=".csv" onChange={handlePeopleCsvFileChange} />
                        <Link onClick={() => handleDownloadTemplate('PeopleTemplate.csv', PEOPLE_CSV_TEMPLATE)}>Download Template</Link>
                    </FormFileUploadContainer>
                    {error && <p style={{ color: 'red' }}>{error}</p>}
                    <Button
                        onClick={(event) => {
                            event.preventDefault();
                            if (!invitationsCsvFile || !peopleCsvFile) {
                                alert('Please upload both CSV files');
                            } else {
                                handleUploadData();
                            }
                        }}
                    >
                        Upload
                    </Button>
                        
                    {uploadResults && (
                        <ResultsContainer>
                            <h3>Upload Results</h3>
                            <ResultItem>Families Created: {uploadResults.families_created}</ResultItem>
                            <ResultItem>People Created: {uploadResults.people_created}</ResultItem>
                            <ResultItem>People Updated: {uploadResults.people_updated}</ResultItem>
                            <ResultItem>Invitations Created: {uploadResults.invitations_created}</ResultItem>
                            <ResultItem>Invitations Updated: {uploadResults.invitations_updated}</ResultItem>
                            <ResultItem>Families Deleted: {uploadResults.families_deleted}</ResultItem>
                            <ResultItem>People Deleted: {uploadResults.people_deleted}</ResultItem>
                            <ResultItem>Invitations Deleted: {uploadResults.invitations_deleted}</ResultItem>
                            <ResultItem>RSVPs Deleted: {uploadResults.rsvps_deleted}</ResultItem>
                        </ResultsContainer>
                    )}
                </Form>
            )}       
        </>
    );
}

export default UploadDataPage;